import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Fade } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import MainLayout from '../../hoc/MainLayout';
import {
  createHandlers,
  handleGenerateReport,
} from './utils/assessmentReportHandlers';

// Component imports
import StudentPatientSelector from './components/StudentPatientSelector';
import DateRangeSelector from './components/DateRangeSelector';
import ShareOptions from './components/ShareOptions';
import GenerateReportButton from './components/GenerateReportButton';
import ConfirmDialog from './components/ConfirmDialog';
import { ACM } from '../../constants/UserRoles';
import AssessmentFilters from './components/AssessmentFilters';
import useAssessmentReportLogic from './hooks/useAssessmentReportLogic';
import AssessmentsList from './components/AssessmentsList';

export default function AssessmentReports() {
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
const [pdfUrl, setPdfUrl] = useState("");
  const {
    // State
    selectedStudent,
    selectedPatient,
    goalType,
    selectedAssessmentType,
    dateRange,
    shareWith,
    selectedGoals,
    confirmDialogOpen,
    pendingAction,
    teachers,
    isGenerating,
    selectedClassrooms,

    // Data
    studentsList,
    patientData,
    preRegisteredUsers,
    filteredStudentGoals,
    studentGoalsData,
    isLoadingGoalData,
    user,
    typeOfUser,
    classrooms,

    // Setters
    setSelectedStudent,
    setSelectedPatient,
    setGoalType,
    setSelectedAssessmentType,
    setDateRange,
    setShareWith,
    setSelectedGoals,
    setConfirmDialogOpen,
    setPendingAction,
    setIsGenerating,
    setSelectedClassrooms,

    // Navigation
    navigate,
  } = useAssessmentReportLogic();

  const {
    handleDateChange,
    handleSelectStudent,
    handleSelectPatient,
    handleRadioToggle,
    handleViewGoal,
    handleSelectGoal,
    handleConfirmReset,
    handleCancelReset,
  } = createHandlers({
    selectedGoals,
    setSelectedGoals,
    setConfirmDialogOpen,
    setPendingAction,
    setDateRange,
    setSelectedStudent,
    setSelectedPatient,
    setGoalType,
    setSelectedAssessmentType,
    goalType,
    selectedAssessmentType,
    navigate,
    setPdfModalOpen,
    setPdfUrl,
  });

  const onGenerateReport = () => {
    handleGenerateReport({
      selectedGoals,
      selectedStudent,
      user,
      filteredStudentGoals,
      shareWith,
      dateRange,
      setIsGenerating,
      setSelectedGoals,
      setShareWith,
      typeOfUser,
      ACM,
    });
  };

  const onConfirmReset = () => {
    handleConfirmReset(pendingAction, selectedAssessmentType);
  };
  const dynamicAssessmentTypes = useMemo(() => {
    if (!studentGoalsData || studentGoalsData.length === 0) return [];
  
    const types = new Set();
  
    studentGoalsData.forEach(student => {
      (student.attachmentData || []).forEach(att => {
        const type = att.docData?.typeOfAssessment;
        if (type) types.add(type);
      });
    });
  
    return Array.from(types); 
  }, [studentGoalsData]);

  // ⭐ Set default selected tab
  useEffect(() => {
    if (
      dynamicAssessmentTypes.length > 0 &&
      (selectedAssessmentType === null || selectedAssessmentType === undefined)
    ) {
      setSelectedAssessmentType(dynamicAssessmentTypes[0]);
    }
  }, [dynamicAssessmentTypes, selectedAssessmentType]);
  
  
  console.log('dynamicAssessmentTypes', dynamicAssessmentTypes);
  return (
    <MainLayout>
      <Box sx={{ p: 2 }}>
        <Fade in timeout={300}>
          <Box>
            {/* Filters Section */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                mb: 2, 
                background: '#FFFFFF',
                border: '1px solid rgba(123, 122, 221, 0.1)',
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Box
                  display='flex'
                  alignItems='flex-start'
                  gap={2}
                  sx={{ 
                    flexWrap: 'wrap',
                    flex: 1,
                  }}
                >
                  <StudentPatientSelector
                    typeOfUser={typeOfUser}
                    selectedStudent={selectedStudent}
                    selectedPatient={selectedPatient}
                    studentsList={studentsList}
                    patientData={patientData}
                    onSelectStudent={handleSelectStudent}
                    onSelectPatient={handleSelectPatient}
                  />

                  <DateRangeSelector
                    dateRange={dateRange}
                    onDateChange={handleDateChange}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Goal Filters */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                mb: 2,
                border: '1px solid rgba(123, 122, 221, 0.1)',
                borderRadius: 2,
                background: '#FFFFFF',
              }}
            >
              <AssessmentFilters
                selectedAssessmentType={selectedAssessmentType}
                selectedGoals={selectedGoals}
                onRadioToggle={handleRadioToggle}
                classrooms={classrooms}
                selectedClassrooms={selectedClassrooms}
                onClassroomChange={setSelectedClassrooms}
                dynamicAssessmentTypes={dynamicAssessmentTypes}
              />
            </Paper>

            {/* Goals List */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                mb: 2,
                border: '1px solid rgba(123, 122, 221, 0.1)',
                borderRadius: 2,
                background: '#FFFFFF',
              }}
            >
              <AssessmentsList
                filteredStudentGoals={filteredStudentGoals}
                isLoadingGoalData={isLoadingGoalData}
                selectedGoals={selectedGoals}
                onViewGoal={handleViewGoal}
                onSelectGoal={handleSelectGoal}
                selectedAssessmentType={selectedAssessmentType}  
                pdfModalOpen={pdfModalOpen}
                pdfUrl={pdfUrl}
                setPdfModalOpen={setPdfModalOpen}
              />
            </Paper>

            {/* Share Options and Generate Button */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 2,
                border: '1px solid rgba(123, 122, 221, 0.1)',
                borderRadius: 2,
                background: '#FFFFFF',
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 3,
              }}>
                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                  <ShareOptions
                    typeOfUser={typeOfUser}
                    selectedStudent={selectedStudent}
                    shareWith={shareWith}
                    teachers={teachers}
                    preRegisteredUsers={preRegisteredUsers}
                    onShareWithChange={setShareWith}
                  />
                </Box>

                <Box sx={{ flex: '0 0 auto', minWidth: 'fit-content' }}>
                  <GenerateReportButton
                    selectedGoals={selectedGoals}
                    isGenerating={isGenerating}
                    onGenerateReport={onGenerateReport}
                  />
                </Box>
              </Box>
            </Paper>

            <ConfirmDialog
              open={confirmDialogOpen}
              onConfirm={onConfirmReset}
              onCancel={handleCancelReset}
            />
          </Box>
        </Fade>
      </Box>
      <ToastContainer />
    </MainLayout>
  );
}
